import { useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { AbsenceBadge } from '@/components/AbsenceBadge';
import { DirectoryOrgChart } from '@/components/DirectoryOrgChart';
import { Modal } from '@/components/Modal';
import { Avatar, PersonCard } from '@/components/PersonCard';
import type { OrgTreeNode } from '@/components/OrgTreeChart';
import { leadBadge, mutedText } from '@/components/personStyles';
import { departmentNodes } from '@/lib/directory/orgTreeNodes';
import { siteUnitBlocks } from '@/lib/directory/siteUnitBlocks';
import {
  directoryPhotoUrl,
  useDirectoryPerson,
  useDirectoryTree,
  type DirectoryMember,
  type DirectoryUnit,
} from '@/lib/directory/useDirectory';
import { useDepartments, useSites } from '@/lib/queries';
import { matchesDuties } from '@/lib/text/dutySearch';
import { matchesPhone, phoneQuery } from '@/lib/text/phoneSearch';

/**
 * 직원찾기 — 헤더의 버튼이 여는 모달. 좌측 조직도에서 사람을 고르면 우측에 명함이 뜬다.
 *
 * 결재선 지정({@link ApprovalLinePicker})과 같은 2단 구성을 쓴다. 그쪽이 이미 "조직도에서 사람을
 * 고른다" 는 동작을 갖고 있어서, 다른 모양을 만들면 같은 일을 두 가지로 배우게 된다.
 *
 * <b>보이는 범위는 서버가 정한다.</b> 이 화면은 받은 것을 그릴 뿐이다 — 인사 항목(생년월일·기술등급
 * 따위)은 애초에 응답에 없다(`DirectoryController`).
 */
export function DirectoryModal({
  onClose,
  initialPersonnelId = null,
  initialQuery = '',
}: {
  onClose: () => void;
  /**
   * 열자마자 명함을 띄울 사람(선택).
   *
   * <p>헤더 버튼으로 열 때는 비어 있다 — 누구를 찾을지는 그때 정한다. 반대로 <b>이미 이 사람을
   * 가리키고 연 경우</b>(현장 연락처에서 이름을 눌렀을 때)는 조직도를 다시 뒤지게 하면 안 된다.
   * 좌측 조직도는 그대로 있어 옆 사람으로 건너갈 수 있다.
   */
  initialPersonnelId?: string | null;
  /**
   * 열자마자 조직도를 좁혀 둘 검색어(선택).
   *
   * <p>{@link initialPersonnelId} 를 <b>모를 때</b> 쓴다 — 이름은 아는데 그 사람의 id 를 못 가진
   * 자리가 있다(옛 서버가 내려주지 않은 줄). 그때 빈 조직도를 띄우면 사람은 방금 본 이름을 다시
   * 쳐야 한다. 동명이인이면 둘 다 보이고, 고르는 것은 사람이 한다 — <b>짐작해서 하나를 열지 않는다.</b>
   */
  initialQuery?: string;
}) {
  const [query, setQuery] = useState(initialQuery);
  const [selectedId, setSelectedId] = useState<string | null>(initialPersonnelId);
  const [openUnits, setOpenUnits] = useState<Record<string, boolean>>({});
  /**
   * 최상단 두 줄(본사·현장)의 접힘. <b>둘 다 닫힌 채로 시작한다</b>(사용자 지시 2026-09-02) —
   * 펼쳐 두면 열자마자 부서·현장 스무 줄이 쏟아져, 트리로 묶은 뜻이 없어진다.
   */
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  /**
   * 스크롤을 맞춰 준 사람. 같은 사람에게 <b>두 번 하지 않는다</b> — 명함이 로드되며 다시 그려질 때마다
   * 목록이 덜컥거리지 않게. (사람을 눌러 고른 경우는 이미 보이는 자리라 {@code 'nearest'} 가 아무 일도
   * 하지 않는다.)
   */
  const scrolledTo = useRef<string | null>(null);
  /**
   * 조직도 도식을 켜 두었는가 (사용자 지시 2026-09-28). <b>기본은 꺼짐</b>이다 — 이 창을 여는 대부분의
   * 이유는 「한 사람의 연락처」 이고, 그때 도식은 한 단계를 더 누르게 만든다.
   */
  const [chartOn, setChartOn] = useState(false);
  const tree = useDirectoryTree(true);
  const { data: departments = [] } = useDepartments();
  const { data: sites = [] } = useSites();
  const person = useDirectoryPerson(selectedId);

  const keyword = query.trim().toLowerCase();

  // 검색은 사람과 소속을 함께 본다. "경영지원" 으로 팀을 찾고 "김" 으로 사람을 찾는 두 가지가
  // 같은 칸에서 되어야 한다 — 어느 쪽으로 찾을지는 쓰는 사람이 그때그때 정한다.
  // 담당업무도 같은 칸이다(사용자 지시 2026-10-14 — 「업무로 찾기」 를 따로 켜지 않는다).
  const units = useMemo(() => filterUnits(tree.data ?? [], keyword), [tree.data, keyword]);

  /**
   * 최상단은 <b>본사와 현장</b> 두 줄이다 (사용자 지시 2026-09-02).
   *
   * <p>종전에는 부서·현장이 한 층에 나란히 서고 줄마다 「부서」·「현장」 이라는 꼬리표가 붙었다.
   * 스무 줄이 평평하게 늘어서서 <b>어디까지가 본사인지</b>가 화면에 없었고, 같은 말을 줄마다
   * 스무 번 되풀이했다. 그 말을 위로 올려 한 번만 적는다 — 부서 줄에서는 꼬리표가 사라진다.
   *
   * <p>본사가 위, 현장이 아래다. 빈 쪽은 줄 자체를 내지 않는다 — 현장이 없는데 「현장 0」 이
   * 서 있으면 접힌 것인지 없는 것인지 알 수 없다.
   */
  const groups = useMemo(
    () =>
      [
        { key: 'HQ', label: '본사', units: units.filter((unit) => unit.unitType !== 'SITE') },
        { key: 'SITE', label: '현장', units: units.filter((unit) => unit.unitType === 'SITE') },
      ].filter((group) => group.units.length > 0),
    [units],
  );

  /**
   * 고른 사람이 들어 있는 소속 — <b>접힌 채로 두지 않는다</b>(2026-08-28).
   *
   * <p>현장 연락처에서 당사 인원을 눌러 이 창을 열면 우측 명함은 떴는데 좌측 조직도는 전부 접혀
   * 있었다. 그러면 그 사람이 <b>어디 소속인지</b>가 화면에 없고, 옆 사람으로 건너가려면 자기가
   * 방금 연 사람을 조직도에서 다시 찾아야 한다. 소속을 펼쳐 두면 그 자리에 선택 표시가 함께 보인다.
   *
   * <p>기본값일 뿐이라 사람이 접으면 접힌다({@code openUnits} 가 먼저다). 겸직·파견이라 소속이
   * 둘이면 <b>둘 다</b> 펼친다 — 어느 한쪽을 고르는 것은 짐작이다.
   */
  const unitsOfSelected = useMemo(() => {
    const keys = new Set<string>();
    if (selectedId === null) return keys;
    for (const unit of tree.data ?? []) {
      if (unit.members.some((member) => member.personnelId === selectedId)) {
        keys.add(`${unit.unitType}:${unit.unitName}`);
      }
    }
    return keys;
  }, [tree.data, selectedId]);

  /**
   * 본사 갈래는 <b>조직도와 같은 트리</b>다 (사용자 지시 2026-10-15). 뼈대는 도식과 같은
   * {@link departmentNodes} 라 두 화면이 같은 층을 보인다 — 「기술부」 처럼 사람 없이 가지만 가진 층도 선다.
   */
  const hqTree = useMemo(
    () =>
      buildHqTree(
        departmentNodes(departments, (tree.data ?? []).filter((unit) => unit.unitType !== 'SITE')),
        tree.data ?? [],
        units,
        keyword,
      ),
    [departments, tree.data, units, keyword],
  );
  const siteUnits = useMemo(() => units.filter((unit) => unit.unitType === 'SITE'), [units]);
  /** 현장 갈래는 국내 진행 · 해외 진행 · 예정 · 평가 · 준공 머리 아래로 선다 — 조직도의 현장 목록과 같다(사용자 지시 2026-10-16). */
  const siteBlocks = useMemo(() => siteUnitBlocks(siteUnits, sites), [siteUnits, sites]);

  /** 목록의 최상단 두 줄. 빈 쪽은 줄 자체를 내지 않는다({@link groups} 와 같은 규칙). */
  const listGroups = [
    { key: 'HQ', label: '본사', size: hqTree.length },
    { key: 'SITE', label: '현장', size: siteUnits.length },
  ].filter((group) => group.size > 0);

  /** 고른 사람이 든 부서와 <b>그 위 부서 전부</b> — 소속만 펼쳐 봐야 상위가 닫혀 있으면 안 보인다. */
  const hqPathOfSelected = useMemo(() => {
    const ids = new Set<string>();
    if (selectedId === null) return ids;
    const walk = (branches: HqBranch[], trail: string[]) => {
      for (const branch of branches) {
        const path = [...trail, branch.id];
        if (branch.members.some((member) => member.personnelId === selectedId)) path.forEach((id) => ids.add(id));
        walk(branch.children, path);
      }
    };
    walk(hqTree, []);
    return ids;
  }, [hqTree, selectedId]);

  /** 고른 사람이 든 최상단 줄. */
  const groupsOfSelected = useMemo(() => {
    const keys = new Set<string>();
    if (hqPathOfSelected.size > 0) keys.add('HQ');
    if ([...unitsOfSelected].some((key) => key.startsWith('SITE:'))) keys.add('SITE');
    return keys;
  }, [hqPathOfSelected, unitsOfSelected]);

  /** 소속 줄 — 최상단이 한 칸 위에 있으므로 들여 쓰고, 본사 트리는 층마다 한 칸씩 더 들인다. */
  const unitButton = (key: string, name: string, open: boolean, depth: number) => (
    <button
      type="button"
      onClick={() => setOpenUnits((prev) => ({ ...prev, [key]: !open }))}
      aria-expanded={open}
      className="w-full flex items-center gap-1.5 py-1.5 rounded-lg text-sm hover:bg-slate-50"
      style={{ color: 'var(--text-primary)', fontWeight: 600, padding: `6px 8px 6px ${22 + depth * INDENT}px` }}
    >
      <Caret open={open} />
      <span className="truncate">{name}</span>
    </button>
  );

  const renderMember = (member: DirectoryMember, unitKey: string, isSite: boolean, depth: number) => {
    const active = member.personnelId === selectedId;
    const dutyHit = keyword !== '' && matchedByDutyOnly(member, keyword);
    // 업무분장(V599) 업무 때문에 걸린 사람 — 이름 옆에 그 사실을 적는다(2026-10-14 지시 · 2026-10-30 전결표 → 업무분장).
    const assignedHit = dutyHit && (member.assignedDuties ?? []).some((d) => matchesDuties(d, keyword));
    return (
      <button
        key={`${unitKey}:${member.personnelId}`}
        type="button"
        /*
          열자마자 가리킨 사람이 목록 <b>아래쪽</b>에 있으면, 소속을 펼쳐도
          보이지 않는다(칸 높이는 380px 로 고정이다). 펼치기만 하고 끝내면
          "선택된 것처럼 보인다" 는 목적이 그 사람에게만 안 지켜진다.
        */
        ref={
          active
            ? (node) => {
                if (!node || scrolledTo.current === member.personnelId) return;
                scrolledTo.current = member.personnelId;
                // 'nearest' 다 — 'center' 는 모달 바깥까지 함께 밀어 올린다.
                node.scrollIntoView({ block: 'nearest' });
              }
            : undefined
        }
        onClick={() => setSelectedId(member.personnelId)}
        aria-current={active ? 'true' : undefined}
        className="w-full flex flex-wrap items-center gap-x-2 rounded-lg text-sm hover:bg-slate-50"
        style={{
          padding: `5px 8px 5px ${44 + depth * INDENT}px`,
          background: active ? '#eff6ff' : 'transparent',
          color: active ? 'var(--accent)' : 'var(--text-secondary)',
          fontWeight: active ? 600 : 400,
          // 선택 표시를 테두리가 아니라 그림자로 넣는다 — 테두리는 글자를 밀어
          // 이름이 2px 씩 움직인다.
          boxShadow: active ? 'inset 2px 0 0 var(--accent)' : undefined,
        }}
      >
        {/* 사진은 트리와 함께 오지 않는다 — 있는 줄만 주소를 물리고,
            브라우저가 보이는 자리부터 천천히 받아 온다. */}
        <Avatar
          name={member.name}
          size={22}
          photoUrl={member.hasPhoto ? directoryPhotoUrl(member.personnelId) : null}
        />
        <span className="truncate">{member.name}</span>
        {member.rank && (
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{member.rank}</span>
        )}
        {member.teamLead && (
          <span style={leadBadge}>{!isSite ? '팀장' : member.position === '현장소장' ? '현장소장' : '현장대리인'}</span>
        )}
        <AbsenceBadge personnelId={member.personnelId} />
        {assignedHit && (
          <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--accent)' }}>- 업무분장</span>
        )}
        {/* 이름으로는 안 걸리고 담당업무로 걸린 사람 — 왜 나왔는지를 이름 아래에 보인다.
            업무분장 업무는 찾기에만 쓰고 보이지 않는다(2026-10-14 지시) — 담당업무 글만 싣는다. */}
        {dutyHit && member.duties && (
          <span
            style={{
              flexBasis: '100%',
              paddingLeft: '30px',
              fontSize: '12px',
              fontWeight: 400,
              color: 'var(--text-secondary)',
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {dutySnippet(member.duties, keyword.split(/\s+/).filter(Boolean))}
          </span>
        )}
      </button>
    );
  };

  /** 본사 트리의 한 가지 — 그 부서 사람이 먼저, 하위 부서가 그 아래. */
  const renderBranch = (branch: HqBranch, depth: number): ReactNode => {
    const key = `HQ:${branch.id}`;
    const open = keyword !== '' || (openUnits[key] ?? hqPathOfSelected.has(branch.id));
    return (
      <div key={key} style={{ marginBottom: '2px' }}>
        {unitButton(key, branch.name, open, depth)}
        {open && branch.members.map((member) => renderMember(member, key, false, depth))}
        {open && branch.children.map((child) => renderBranch(child, depth + 1))}
      </div>
    );
  };

  /** 검색칸. 목록과 도식이 <b>같은 칸</b>을 쓴다 — 도식도 좁혀진 결과를 그린다. */
  const searchBox = (
    <input
      className="input-field"
      style={{ padding: '8px 10px', fontSize: '13px', marginBottom: '8px' }}
      placeholder="키워드 검색"
      value={query}
      onChange={(event) => setQuery(event.target.value)}
      aria-label="직원 검색"
      autoFocus
    />
  );

  return (
    <Modal
      title="직원찾기"
      onClose={onClose}
      // 도식은 가지가 옆으로 뻗는다 — 목록보다 넓은 창을 쓴다.
      maxWidth={chartOn ? '1280px' : '900px'}
      /*
        제목 줄의 스위치 (사용자 지시 2026-09-28: 「X표 왼쪽에 조직도 On/Off」). 이 앱의 모달 버튼은
        제목 줄이 규칙이고, 자리는 {@code Modal} 이 정한다 — 좁은 화면에서는 바닥으로 내려간다.
      */
      actions={
        <button
          type="button"
          className={chartOn ? 'btn-primary' : 'btn-secondary'}
          aria-pressed={chartOn}
          onClick={() => setChartOn(!chartOn)}
          style={{ fontSize: '12px', padding: '4px 10px' }}
        >
          조직도
        </button>
      }
    >
      {chartOn ? (
        /*
          도식 — 본사·현장이 뿌리, 그 아래 임원실·팀·현장이 가지다. 가지를 누르면 그 소속이 직급별로
          펼쳐지고, 사람을 누르면 도식을 접고 명함을 띄운다(그 사람을 보려고 누른 것이다).
        */
        <div style={panelStyle}>
          {searchBox}
          {tree.isPending ? (
            <p style={mutedText}>불러오는 중…</p>
          ) : groups.length === 0 ? (
            <p style={mutedText}>
              {keyword ? '검색 결과가 없습니다.' : '조직도가 비어 있습니다. (인력·소속 등록 필요)'}
            </p>
          ) : (
            <DirectoryOrgChart
              axes={groups}
              selectedId={selectedId}
              searching={keyword !== ''}
              onPick={(personnelId) => {
                setSelectedId(personnelId);
                setChartOn(false);
              }}
            />
          )}
        </div>
      ) : (
      /* 좁은 화면에서는 위아래로 쌓는다 — 폭이 모자란 채 2단이면 이름도 연락처도 잘린다. */
      <div className="grid gap-3 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        {/* ── 좌: 조직도 ─────────────────────────────────────────────────── */}
        <div style={panelStyle}>
          {searchBox}

          <div style={{ height: '380px', overflowY: 'auto' }}>
            {tree.isPending ? (
              <p style={mutedText}>불러오는 중…</p>
            ) : hqTree.length === 0 && siteUnits.length === 0 ? (
              <p style={mutedText}>
                {keyword ? '검색 결과가 없습니다.' : '조직도가 비어 있습니다. (인력·소속 등록 필요)'}
              </p>
            ) : (
              listGroups.map((group) => {
                /*
                  기본은 닫힘이다. 두 경우에만 저절로 열린다 —
                    ① 검색 중. 걸린 사람이 접힌 줄 안에 숨으면 찾은 보람이 없다.
                    ② 사람을 가리키고 연 경우. 그 사람의 소속을 펼쳐 두기로 한 결정(2026-08-28)이
                       최상단이 닫혀 있으면 화면에 닿지 않는다.
                */
                const groupOpen =
                  keyword !== '' || (openGroups[group.key] ?? groupsOfSelected.has(group.key));

                return (
                  <div key={group.key} style={{ marginBottom: '4px' }}>
                    <button
                      type="button"
                      onClick={() => setOpenGroups((prev) => ({ ...prev, [group.key]: !groupOpen }))}
                      aria-expanded={groupOpen}
                      className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm hover:bg-slate-50"
                      style={{ color: 'var(--text-primary)', fontWeight: 700 }}
                    >
                      <Caret open={groupOpen} />
                      <span className="truncate">{group.label}</span>
                    </button>

                    {groupOpen &&
                      (group.key === 'HQ'
                        ? hqTree.map((branch) => renderBranch(branch, 0))
                        : siteBlocks.map((block) => (
                            <div key={block.key}>
                              {block.label && (
                                <div style={{ padding: '6px 8px 2px 22px', fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)' }}>
                                  {block.label}
                                </div>
                              )}
                              {block.units.map((unit) => {
                                const key = `${unit.unitType}:${unit.unitName}`;
                                // 고른 사람의 소속은 펼친 채로 연다({@link unitsOfSelected}).
                                const open = keyword !== '' || (openUnits[key] ?? unitsOfSelected.has(key));
                                return (
                                  <div key={key} style={{ marginBottom: '2px' }}>
                                    {unitButton(key, unit.unitName, open, 0)}
                                    {open && unit.members.map((member) => renderMember(member, key, true, 0))}
                                  </div>
                                );
                              })}
                            </div>
                          )))}
                  </div>
                );
              })
            )}
          </div>

        </div>

        {/* ── 우: 명함 ───────────────────────────────────────────────────── */}
        <div style={panelStyle}>
          {selectedId === null ? (
            <p style={{ ...mutedText, padding: '150px 0', textAlign: 'center' }}>
              왼쪽에서 직원을 선택하세요.
            </p>
          ) : person.isPending ? (
            <p style={{ ...mutedText, padding: '150px 0', textAlign: 'center' }}>불러오는 중…</p>
          ) : person.isError || !person.data ? (
            <p style={{ ...mutedText, padding: '150px 0', textAlign: 'center' }}>
              직원 정보를 불러오지 못했습니다.
            </p>
          ) : (
            <PersonCard person={person.data} collapseMatrixDuties matrixKeyword={keyword} />
          )}
        </div>
      </div>
      )}
    </Modal>
  );
}

/** 이름·직급·직책·이메일·연락처로는 안 걸리고 담당업무로만 걸린 사람인가. */
function matchedByDutyOnly(member: DirectoryMember, keyword: string): boolean {
  const byPerson =
    [member.name, member.rank, member.position, member.email]
      .filter(Boolean)
      .some((text) => (text as string).toLowerCase().includes(keyword)) ||
    matchesPhone(member.phone, phoneQuery(keyword));
  return !byPerson && matchesDuties(dutyTextOf(member), keyword);
}

/**
 * 찾을 업무 글 — 담당업무와 업무분장(V599) 업무를 한 줄씩. 전결표 업무는 2026-10-30 에 여기서 빠졌다(「전결표 업무 대신
 * 담당 업무」). 서버가 재시작 전이면 업무분장 업무가 없다.
 */
function dutyTextOf(member: DirectoryMember): string {
  // 공개로 둔 학력(V615)도 함께 찾는다 — 「한양대」 를 치면 그 학교 사람이 걸린다(사용자 지시 2026-11-02).
  return [member.duties, ...(member.assignedDuties ?? []), ...(member.educations ?? [])].filter(Boolean).join(' · ');
}

/**
 * 담당업무 글에서 첫 낱말이 걸린 자리 앞뒤를 잘라, 낱말마다 굵게 칠한다. 서버는 공백을 지우고 견주므로
 * 「자재발주」 로 찾았는데 글에는 「자재 발주」 라 화면에서 못 짚는 경우가 있다 — 그때는 글 머리를 보인다.
 */
function dutySnippet(text: string, words: string[]): ReactNode {
  const flat = text.replace(/\s+/g, ' ');
  const first = words[0] ? flat.toLowerCase().indexOf(words[0].toLowerCase()) : -1;
  const start = first > 20 ? first - 20 : 0;
  const shown = (start > 0 ? '…' : '') + flat.slice(start);

  const needles = words.map((word) => word.toLowerCase());
  const shownLower = shown.toLowerCase();
  const parts: ReactNode[] = [];
  let cursor = 0;
  while (cursor < shown.length) {
    let hitAt = -1;
    let hitLength = 0;
    for (const needle of needles) {
      const at = shownLower.indexOf(needle, cursor);
      if (at >= 0 && (hitAt < 0 || at < hitAt)) {
        hitAt = at;
        hitLength = needle.length;
      }
    }
    if (hitAt < 0) {
      parts.push(shown.slice(cursor));
      break;
    }
    if (hitAt > cursor) parts.push(shown.slice(cursor, hitAt));
    parts.push(
      <strong key={hitAt} style={{ color: 'var(--accent)', fontWeight: 700 }}>
        {shown.slice(hitAt, hitAt + hitLength)}
      </strong>,
    );
    cursor = hitAt + hitLength;
  }
  return parts;
}

function Caret({ open }: { open: boolean }) {
  return (
    <svg
      width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', color: 'var(--text-muted)', flexShrink: 0 }}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

/**
 * 검색어로 트리를 좁힌다. 소속 이름이 걸리면 그 단위는 <b>구성원 전원</b>을 남긴다 —
 * "경영지원팀" 을 친 사람이 보고 싶은 것은 그 팀 전체이지 이름에 그 글자가 든 사람이 아니다.
 *
 * <p>숫자만 친 검색어는 <b>휴대전화</b>로도 본다(사용자 지시 2026-09-23). 하이픈을 지우고 대므로
 * 「010-1234-5678」 도 「5678」 도 같은 사람을 찾는다. 소속 이름이 숫자로 걸리는 일은 없으니
 * 단위 이름 쪽에는 걸지 않는다.
 *
 * <p>이메일도 본다(사용자 지시 2026-10-14) — 「kim@」 처럼 주소 일부로 찾는다.
 *
 * <p>담당업무도 본다(사용자 지시 2026-10-14) — 「발주」 를 치면 발주를 맡은 사람이 그 소속 아래 선다.
 */
function filterUnits(units: DirectoryUnit[], keyword: string): DirectoryUnit[] {
  if (!keyword) return units;

  const digits = phoneQuery(keyword);

  return units
    .map((unit) => {
      if (unit.unitName.toLowerCase().includes(keyword)) return unit;

      const members = unit.members.filter(
        (member) =>
          [member.name, member.rank, member.position, member.email]
            .filter(Boolean)
            .some((text) => (text as string).toLowerCase().includes(keyword)) ||
          matchesPhone(member.phone, digits) ||
          matchesDuties(dutyTextOf(member), keyword),
      );
      return { ...unit, members };
    })
    .filter((unit) => unit.members.length > 0);
}

/** 본사 트리 한 층마다 더 들이는 폭(px). */
const INDENT = 16;

/** 본사 트리의 가지 — 부서 하나와 그 사람, 하위 부서. */
type HqBranch = { id: string; name: string; members: DirectoryMember[]; children: HqBranch[] };

/**
 * 조직도 칸({@link departmentNodes})을 목록용 가지로 엮는다. 사람은 주소록에서 <b>이름으로</b> 맞춘다 —
 * 도식의 사람 칸 타입은 담당업무를 싣지 않는다.
 *
 * <p>검색 중이면 {@link filterUnits} 가 남긴 사람만 싣고, 사람도 하위 가지도 없는 가지는 뺀다 — 걸린
 * 사람의 상위 부서는 그 길로 남는다. <b>부서 이름이 걸리면 그 아래 전부</b>를 사람째 남긴다 —
 * 「기술부」 를 친 사람이 보고 싶은 것은 기술부 아래 팀 전체다. 검색이 없을 때도 사람이 하나도 없는
 * 가지는 세우지 않는다(펼쳐도 빈 줄이다).
 */
function buildHqTree(nodes: OrgTreeNode[], all: DirectoryUnit[], filtered: DirectoryUnit[], keyword: string): HqBranch[] {
  const allByName = new Map(all.filter((unit) => unit.unitType !== 'SITE').map((unit) => [unit.unitName, unit.members]));
  const hitByName = new Map(filtered.filter((unit) => unit.unitType !== 'SITE').map((unit) => [unit.unitName, unit.members]));
  const ids = new Set(nodes.map((node) => node.id));
  const childrenOf = new Map<string | null, OrgTreeNode[]>();
  for (const node of nodes) {
    // 목록에 없는 상위(꺼진 부서 아래 남은 팀)는 뿌리로 세운다 — 도식({@code OrgTreeChart})과 같은 규칙.
    const parent = node.parentId && ids.has(node.parentId) ? node.parentId : null;
    childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), node]);
  }

  const build = (parentId: string | null, whole: boolean): HqBranch[] =>
    (childrenOf.get(parentId) ?? []).flatMap((node) => {
      const nameHit = whole || (keyword !== '' && node.name.toLowerCase().includes(keyword));
      const members = (keyword === '' || nameHit ? allByName.get(node.name) : hitByName.get(node.name)) ?? [];
      const children = build(node.id, nameHit);
      return members.length > 0 || children.length > 0 ? [{ id: node.id, name: node.name, members, children }] : [];
    });

  // 맨 위 부서는 하위와 나란히 선다(사용자 지시 2026-10-15) — 회장을 숨기면 대표이사가 뿌리가 되어
  // 회사 전체가 한 층 더 들어간다. 「본사 > 대표이사 · 본부 · 부서」 로 편다. 사람 없는 뿌리는 빈 줄이라 뺀다.
  return build(null, false).flatMap((root) =>
    root.children.length === 0
      ? [root]
      : [...(root.members.length > 0 ? [{ ...root, children: [] }] : []), ...root.children],
  );
}

const panelStyle: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: '10px',
  padding: '10px',
  minWidth: 0,
};

